/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListEmployeeAIDetectionsRequest,
  ListEmployeeAIDetectionsSecurity,
} from "../models/operations/listemployeeaidetections.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildEmployeeAIDetectionsQuery,
  EmployeeAIDetectionsQueryData,
  prefetchEmployeeAIDetections,
  queryKeyEmployeeAIDetections,
} from "./employeeAIDetections.core.js";
export {
  buildEmployeeAIDetectionsQuery,
  type EmployeeAIDetectionsQueryData,
  prefetchEmployeeAIDetections,
  queryKeyEmployeeAIDetections,
};

export type EmployeeAIDetectionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listEmployeeAIDetections access
 *
 * @remarks
 * List AI tools detected for one enrolled employee in the active organization. The employee email is required so project viewers cannot broaden the request into an organization-wide inventory. Linked alias emails are folded to the canonical identity. Requires project:read on the active project; the access decision on each row carries its state but not who recorded it, when, or why.
 */
export function useEmployeeAIDetections(
  request: ListEmployeeAIDetectionsRequest,
  security?: ListEmployeeAIDetectionsSecurity | undefined,
  options?: QueryHookOptions<
    EmployeeAIDetectionsQueryData,
    EmployeeAIDetectionsQueryError
  >,
): UseQueryResult<
  EmployeeAIDetectionsQueryData,
  EmployeeAIDetectionsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildEmployeeAIDetectionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listEmployeeAIDetections access
 *
 * @remarks
 * List AI tools detected for one enrolled employee in the active organization. The employee email is required so project viewers cannot broaden the request into an organization-wide inventory. Linked alias emails are folded to the canonical identity. Requires project:read on the active project; the access decision on each row carries its state but not who recorded it, when, or why.
 */
export function useEmployeeAIDetectionsSuspense(
  request: ListEmployeeAIDetectionsRequest,
  security?: ListEmployeeAIDetectionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    EmployeeAIDetectionsQueryData,
    EmployeeAIDetectionsQueryError
  >,
): UseSuspenseQueryResult<
  EmployeeAIDetectionsQueryData,
  EmployeeAIDetectionsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildEmployeeAIDetectionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setEmployeeAIDetectionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      userEmail: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: EmployeeAIDetectionsQueryData,
): EmployeeAIDetectionsQueryData | undefined {
  const key = queryKeyEmployeeAIDetections(...queryKeyBase);

  return client.setQueryData<EmployeeAIDetectionsQueryData>(key, data);
}

export function invalidateEmployeeAIDetections(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      userEmail: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "listEmployeeAIDetections",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllEmployeeAIDetections(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listEmployeeAIDetections"],
  });
}
